import { TextFieldProps } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { SearchOption } from '../../../../../../../../components/Search/interfaces/search-option.enum';
import LangEnum from '../../../../../../../../lang/LangEnum';
import useProductActionsStore from '../../../../../../../../stores/ProductActionsStore/productActions.store';
import { OrderProductsTestIds } from '../../../../../../../../utils/testIds/OrderProducts.testIds';
import { AvailabilityEnum } from '../../../../../../../products/ProductTypes';
import { useTranslation } from '../../../../../../../translation/TranslationContext';
import { useOrderProductsLayoutContext } from '../../../../../../contexts/OrderProductsLayout.context';
import { SHOW_TOPBAR_ADD_PRODUCT } from '../../../../../../orders.constants';
import { buildParamsToUpdateItemFormat } from '../../../../../orderProducts.utils';
import { showStock } from '../../../../utils/productGrid.utils';
import useOrderProductGridContext from '../../../contexts/OrderProductGrid.context';
import { selectCellDisabled, selectCellError } from '../../../store/gridStore.selectors';
import { useCell } from '../../../store/useCell';
import { ProductInputProps } from '../types/productInput.types';

const GRID_INPUT_SELECTOR = `[test-id^="${OrderProductsTestIds.GRID_LINE}-"] input[type="number"]:not(:disabled)`;

const useProductInput = ({ gridItem, suggestedGridItem }: ProductInputProps) => {
  const { translate } = useTranslation();
  const { hasTouchGrid, isGridInputReadOnly } = useOrderProductsLayoutContext();
  const {
    product,
    getLimit,
    emptyGrid,
    readMode,
    disableAll,
    onClickEnter,
    getStockValue,
    orderViewMode,
    showSuggestedGrid,
    lastAvailableGrid,
    firstAvailableGrid,
    productAddedByActions,
    stackItem,
    copiedGrid,
    removeCopiedGrid,
  } = useOrderProductGridContext();

  const combinationKey = product.combinationKey ?? '';
  const { api: gridStoreApi, cell } = useCell({ combinationKey, skuId: gridItem.skuId ?? '' });
  const isStoreBacked = !!gridStoreApi && !!combinationKey;

  const { addInputRef, setGridInputRef, setSelectedOption, setNotificationTimerIsValid } =
    useProductActionsStore(
      useShallow((state) => ({
        setSelectedOption: state.setSelectedOption,
        setGridInputRef: state.setGridInputRef,
        addInputRef: state.addInputRef,
        setNotificationTimerIsValid: state.setNotificationTimerIsValid,
      })),
    );

  const inputRef = useRef<HTMLInputElement>(null);

  const max = getLimit({
    availableAmount: gridItem.stock?.availableAmount,
    availability: product.availability,
  });
  const stockValue = getStockValue({
    availableAmount: gridItem.stock?.availableAmount,
    availability: product.availability,
  });
  const isNeededActionFocused =
    !emptyGrid && productAddedByActions && lastAvailableGrid?.skuId === gridItem.skuId;
  const isNeededSaveRef =
    !emptyGrid && productAddedByActions && gridItem.skuId === firstAvailableGrid?.skuId;

  const storeQuantity = isStoreBacked ? (cell?.quantity ?? 0) : (gridItem.orderItemQuantity ?? 0);
  const quantity = storeQuantity;
  const suggestedGridItemQuantity = suggestedGridItem?.orderItemQuantity;
  const mustShowSuggestedGrid = showSuggestedGrid && quantity === 0;

  const placeholder =
    mustShowSuggestedGrid && !!suggestedGridItemQuantity
      ? suggestedGridItemQuantity.toString()
      : '';

  const isEmptyAvailability = product.availability === AvailabilityEnum.EMPTY;

  const storeError = selectCellError({
    quantity: storeQuantity,
    gridItem,
    availability: product.availability,
    orderViewMode,
  });
  const storeDisabled = selectCellDisabled({
    quantity: storeQuantity,
    gridItem,
    error: storeError,
    availability: product.availability,
    readMode,
    disableAll,
    orderViewMode,
  });

  const disabled = storeDisabled || isEmptyAvailability;
  const emptyAvailabilityDisabledTooltip = isEmptyAvailability
    ? translate(LangEnum.ADD_ORDER_PRODUCTS_EMPTY_AVAILABILITY_TOOLTIP)
    : undefined;

  const onChangeGrid = useCallback(
    (value: number) => {
      if (!gridItem.skuId) {
        return;
      }

      const params = {
        value,
        skuId: gridItem.skuId,
        orderItemId: gridItem.orderItemId,
        orderConditionId: gridItem.orderConditionId,
        deliveryWindowId: product.deliveryWindowId,
      };

      if (copiedGrid.grid.length > 0) {
        removeCopiedGrid();
      }

      stackItem(buildParamsToUpdateItemFormat(params, product.availability));
    },
    [
      gridItem,
      stackItem,
      copiedGrid.grid.length,
      removeCopiedGrid,
      product.deliveryWindowId,
      product.availability,
    ],
  );

  const mustShowStock = showStock({
    viewMode: orderViewMode,
    availability: product.availability,
    availableForSale: !!gridItem.stock?.availableForSale,
    stockValue,
  });

  const focusAdjacentGridInput = useCallback((isBackwards: boolean) => {
    const currentInput = inputRef.current;
    if (!currentInput) {
      return false;
    }

    const gridInputs = Array.from(document.querySelectorAll<HTMLInputElement>(GRID_INPUT_SELECTOR));
    const currentIndex = gridInputs.indexOf(currentInput);
    if (currentIndex < 0) {
      return false;
    }

    const targetInput = gridInputs[currentIndex + (isBackwards ? -1 : 1)];
    if (!targetInput) {
      return false;
    }

    targetInput.focus();
    return true;
  }, []);

  const handleTabKey = useCallback(
    (event: React.KeyboardEvent) => {
      if (isNeededActionFocused && SHOW_TOPBAR_ADD_PRODUCT) {
        event.preventDefault();
        setSelectedOption(SearchOption.ADD);
        setNotificationTimerIsValid(false);
        addInputRef?.current?.focus();
        return;
      }

      if (focusAdjacentGridInput(event.shiftKey)) {
        event.preventDefault();
      }
    },
    [
      addInputRef,
      focusAdjacentGridInput,
      isNeededActionFocused,
      setNotificationTimerIsValid,
      setSelectedOption,
    ],
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<any>) => {
      if (event.key === 'Enter' && onClickEnter) {
        onClickEnter();
        return;
      }

      if (event.key === 'Tab') {
        handleTabKey(event);
      }
    },
    [handleTabKey, onClickEnter],
  );

  const onBlur = useCallback(() => {
    setNotificationTimerIsValid(false);
  }, [setNotificationTimerIsValid]);

  const textInputProps: TextFieldProps = useMemo(
    () => ({
      placeholder,
      error: storeError,
      onKeyDown: handleKeyPress,
      onChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
        onChangeGrid(+event.target.value),
      onBlur,
    }),
    [placeholder, storeError, handleKeyPress, onChangeGrid, onBlur],
  );

  useEffect(() => {
    if (isNeededSaveRef) {
      setGridInputRef(inputRef);
    }
  }, []);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return;
    }
    const isFocused = document.activeElement === input;
    const hasPendingEdit = isStoreBacked && !!cell?.isPending;
    if (isFocused && hasPendingEdit) {
      return;
    }
    const nextValue = quantity === 0 ? '' : String(quantity);
    if (input.value !== nextValue) {
      input.value = nextValue;
    }
  }, [quantity, cell?.isPending, isStoreBacked]);

  return {
    disabled,
    quantity,
    stockValue,
    inputRef,
    maxValue: max,
    textInputProps,
    showStock: mustShowStock,
    hasTouchLayout: hasTouchGrid,
    isReadOnly: isGridInputReadOnly,
    productColorAvailability: product.availability,
    tooltipMessage: emptyAvailabilityDisabledTooltip,
  };
};

export default useProductInput;
